import type { ReactNode } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';
import { Brand } from '@/components/Brand';
import { StarLattice } from '@/components/StarLattice';

/** Which intro slide the brand panel shows: its picture and its three lines. */
export type AuthSlide = 'recite' | 'teacher' | 'journey' | 'family';

/**
 * The signed-out pages' frame — the approved sign-in mock (redesign 2026-10):
 * the form in its own column, and beside it an inset, rounded brand panel
 * with the ivory mark, one intro illustration and its three lines. The star
 * lattice sits only in the panel's far corner.
 *
 * On a narrow screen the panel is dropped and the brand lockup rides above
 * the form instead, so the brand is never lost.
 *
 * The language switcher stays OUTSIDE the card: it is chrome for the page,
 * not a control belonging to the form.
 */
export function AuthLayout({
  cardTestId, slide, children,
}: {
  cardTestId: string;
  slide: AuthSlide;
  children: ReactNode;
}) {
  const { t } = useLanguage();
  return (
    <main className="relative flex min-h-screen">
      <div className="absolute start-4 top-4 z-10">
        <LanguageSwitcher />
      </div>

      <section className="flex w-full shrink-0 items-center justify-center px-4 py-16 lg:w-[600px] lg:px-6">
        <div data-testid={cardTestId} className="w-full max-w-[420px]">
          {/* The panel carries the brand on a wide screen. */}
          <div className="mb-10 lg:hidden">
            <Brand size="lg" stacked />
          </div>
          {children}
        </div>
      </section>

      <section
        data-testid="auth-panel"
        aria-hidden="true"
        className="hero-card m-4 hidden flex-1 flex-col items-center justify-center gap-6 rounded-[32px] p-14 text-center lg:flex"
      >
        {/* A soft light from above, behind the mark. */}
        <div className="pointer-events-none absolute -top-44 left-[-120px] right-[-120px] h-[520px] bg-[radial-gradient(closest-side,rgba(255,255,255,0.16),transparent)]" />
        <StarLattice className="h-60 w-72" />
        <img
          // Lime, as on the night app icon the owner chose (2026-10-09).
          src="/brand/mark-lime.png"
          alt=""
          className="relative h-24 w-auto drop-shadow-[0_0_24px_rgba(198,198,50,0.35)]"
        />
        {/* The motto under the large centred mark, as on the app icon (owner, 2026-10-10). */}
        <p data-testid="auth-panel-motto" className="relative -mt-4 text-2xl font-semibold text-white">{t('brand.motto')}</p>
        <div className="relative h-[290px] w-[290px] overflow-hidden rounded-[44px] shadow-[0_18px_40px_rgba(0,0,0,0.25)]">
          <img src={`/intro/${slide}.png`} alt="" className="h-full w-full object-cover" />
        </div>
        <div className="relative max-w-[420px]">
          <p className="text-[15px] font-semibold text-lime">{t(`intro.${slide}.kicker`)}</p>
          <p className="mt-1.5 text-[30px] font-semibold leading-tight">{t(`intro.${slide}.title`)}</p>
          <p className="mt-2 text-[17px] leading-relaxed opacity-80 [text-wrap:balance]">{t(`intro.${slide}.body`)}</p>
        </div>
      </section>
    </main>
  );
}

/** The field look shared by the signed-out forms: 56px, soft fill, a teal ring on focus. */
export const authField = 'h-14 w-full rounded-2xl border-[1.5px] border-hair bg-surface px-4 text-[17px] text-ink outline-none transition-colors placeholder:text-helper focus:border-primary';

/** The one main action on a signed-out page. */
export const authButton = 'inline-flex h-14 w-full items-center justify-center gap-2 rounded-full bg-primary text-[17px] font-semibold text-primary-foreground transition-opacity disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-ground';
